  import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress, Container, Typography } from '@mui/material';
import authService from './services/authService';

// Import components
import Dashboard from './components/Dashboard';
import NotFound from './components/NotFound';
import VendorLogin from './components/lis-portal/VendorLogin';
import ApiKeyManager from './components/lis-portal/ApiKeyManager';
import WebhookSecretManager from './components/lis-portal/WebhookSecretManager';
import UsageStatistics from './components/lis-portal/UsageStatistics';
import ApiExamples from './components/lis-portal/ApiExamples';
import LabManager from './components/lis-portal/LabManager';
import SupportTickets from './components/lis-portal/SupportTickets';
import Account from './components/lis-portal/Account';
import OrganismMapping from './components/lis-portal/OrganismMapping';
import IntegrationSetup from './components/lis-portal/IntegrationSetup';
import FileUploadManager from './components/lis-portal/FileUploadManager';
import TeamManager from './components/lis-portal/TeamManager';
import AccessRequests from './components/lis-portal/AccessRequests';
import Layout from './components/Layout';
import ForceChangePassword from './components/ForceChangePassword';

// Import the new documentation page components
import LisIntegrationGuidePage from './pages/lis-portal/LisIntegrationGuidePage';
import VendorApiGuidePage from './pages/lis-portal/VendorApiGuidePage';
import DocumentationGuidePage from './pages/lis-portal/DocumentationGuidePage';

// Placeholder component for routes that are not yet implemented
const PlaceholderComponent = ({ title }: { title: string }) => (
  <Container sx={{ py: 4 }}>
    <Box sx={{ textAlign: 'center', py: 8 }}>
      <Typography variant="h4" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body1" color="text.secondary">
        This feature is coming soon. Please check back later.
      </Typography>
    </Box>
  </Container>
);

// Loading component
const Loading = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <CircularProgress />
  </Box>
);

const AppRoutes = () => {
  // AuthGuard component to protect routes
  const AuthGuard = ({ children }: { children: React.ReactNode }) => {
    return authService.isAuthenticated() ? <>{children}</> : <Navigate to="/login" />;
  };

  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<VendorLogin />} />
        <Route path="/force-change-password" element={<ForceChangePassword />} />
        
        {/* Protected routes */}
        <Route path="/" element={<AuthGuard><Layout /></AuthGuard>}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          
          {/* LIS Portal routes */}
          <Route path="lis-portal/labs" element={<LabManager />} />
          <Route path="lis-portal/integration-setup" element={<IntegrationSetup />} />
          <Route path="lis-portal/organism-mapping" element={<OrganismMapping />} />
          <Route path="lis-portal/files" element={<FileUploadManager />} />
          <Route path="lis-portal/api-keys" element={<ApiKeyManager />} />
          <Route path="lis-portal/webhook-secrets" element={<WebhookSecretManager />} />
          <Route path="lis-portal/usage" element={<UsageStatistics />} />
          <Route path="lis-portal/support-tickets" element={<SupportTickets />} />
          <Route path="lis-portal/team" element={<TeamManager />} />
          <Route path="lis-portal/access-requests" element={<AccessRequests />} />
          
          {/* Documentation Routes */}
          <Route path="lis-portal/documentation" element={<DocumentationGuidePage />} />
          
          {/* Use the existing IntegrationGuide component or our new page component */}
          <Route path="lis-portal/integration-guide" element={<LisIntegrationGuidePage />} />
          <Route path="lis-portal/vendor-api-guide" element={<VendorApiGuidePage />} />
          <Route path="lis-portal/api-examples" element={<ApiExamples />} />
          <Route path="lis-portal/reports-guide" element={<PlaceholderComponent title="Reports Guide" />} />
          <Route path="lis-portal/support" element={<PlaceholderComponent title="Support & Resources" />} />
          
          {/* Account */}
          <Route path="account" element={<Account />} />
        </Route>
        
        {/* 404 route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes; 